import React, { useEffect, useMemo, useState } from 'react';
import '../../../styles/admin/User.css';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Users } from '../../../models/User.tsx';
import { TbArrowBigLeftFilled } from "react-icons/tb";
import { formatNumbers, isFreePlayBet } from '../../../helpers/functions.tsx';
import { Modal } from 'bootstrap';
import { ImCalculator } from "react-icons/im";
import { FaCaretRight } from "react-icons/fa";
import { runTransaction, doc, Timestamp, collection } from "firebase/firestore";
import db from '../../../services/Firebase.js';
import { logUserActivity } from '../../../services/player/LoginService.tsx';
import { useUser } from '../../../context/UserContext.tsx';
import DataPlaceholder from '../../DataPlaceHolder.tsx';
import { requestAgentSummaryRefresh } from '../../../helpers/agentSummaryRefresh.tsx';
import { subscribeCache } from '../../../helpers/queryCache.tsx';
import {
    peekVisitedUser,
    peekVisitedUserProfile,
    peekVisitedUserProfileForDisplay,
    visitedUserProfileCacheKey,
} from '../../../helpers/adminVisitedUserCache.tsx';
import { formatAdminFiguresShortDate } from '../../../helpers/adminFiguresTime.tsx';
import { agentCanAdjustBalances, isBlockedBalanceAction } from '../../../services/player/SportService.tsx';
import { useAgentPermissions } from '../../../helpers/useAgentPermissions.tsx';

const RETURN_FROM_ROUTES: Record<string, { to: string; activity: string }> = {
    PlayerList: { to: '/weekly-figures', activity: 'View Weekly Figures' },
    Analytics: { to: '/analytics', activity: 'View Closing Line Analysis' },
};

const matchesRouteUser = (u: Users | null | undefined, routeUser: string | undefined) =>
    !!u && !!routeUser && u.userName?.toLowerCase() === routeUser.toLowerCase();

const TableUser = ({visitedUser, isVisitedUserLoading, onUserUpdated,}: 
    { visitedUser: Users | null; isVisitedUserLoading: boolean; onUserUpdated: () => void;}) => {

    const { user: currentUser, userIpData: userIpDataAdmin, pendingBet } = useUser();
    const { allowCreditDebitAdjustments } = useAgentPermissions(currentUser);
    const balanceActor = currentUser ? { ...currentUser, allowCreditDebitAdjustments } : currentUser;
    const canAdjustBalances = agentCanAdjustBalances(balanceActor);
    const { user: routeUser } = useParams<{ user: string }>();
    const location = useLocation();
    const from = (location.state as { from?: string })?.from;
    const returnFrom = from ? RETURN_FROM_ROUTES[from] : undefined;
    const initialCachedUser = routeUser ? peekVisitedUser(routeUser) : undefined;
    const [formData, setFormData] = useState<Users | null>(() =>
        initialCachedUser ?? (matchesRouteUser(visitedUser, routeUser) ? visitedUser : null)
    );
    const [, setPreviousBalance] = useState<number>(0);
    const [formTransaction, setFormTransaction] = useState({
        createDate: formatAdminFiguresShortDate(),
        agent: '',
        customer: '',
        transactionType: 'deposit',
        description: 'Customer Deposit',
        amount: '',
        createBy: '',
    })
    const [isSaving, setIsSaving] = useState(false);
    const [, setProfileCacheTick] = useState(0);

    useEffect(() => {
        if (!routeUser) return;
        const key = visitedUserProfileCacheKey(routeUser);
        return subscribeCache<Users>(key, () => {
            setProfileCacheTick(t => t + 1);
        });
    }, [routeUser]);

    const loadUserData = async () => {
        onUserUpdated();
    };

    useEffect(() => {
        if (!visitedUser || !matchesRouteUser(visitedUser, routeUser)) return;
        setFormData(visitedUser);
        setPreviousBalance(visitedUser.balance ?? 0);
    }, [visitedUser, routeUser]);

    useEffect(() => {
        if (!routeUser) return;
        const cached = peekVisitedUser(routeUser);
        if (cached && matchesRouteUser(cached, routeUser)) {
            setFormData(cached);
            setPreviousBalance(cached.balance ?? 0);
        } else if (!matchesRouteUser(visitedUser, routeUser)) {
            setFormData(null);
        }
        // Solo al cambiar de usuario en la ruta; no re-leer caché al actualizar visitedUser.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [routeUser]);

    const headerPendingBets = pendingBet;

    const totalPendingAmount = useMemo(() => {
        return headerPendingBets?.reduce((sum: number, bet: any) => {
            if (isFreePlayBet(bet)) return sum;
            return sum + (typeof bet.bet === 'number' ? bet.bet : 0);
        }, 0) ?? 0;
    }, [headerPendingBets]);

    const cachedUser = routeUser ? peekVisitedUser(routeUser) : undefined;
    const freshProfile = routeUser ? peekVisitedUserProfileForDisplay(routeUser) : undefined;
    const staleProfile = routeUser ? peekVisitedUserProfile(routeUser) : undefined;
    const displayUser =
        (matchesRouteUser(visitedUser, routeUser) ? visitedUser : undefined) ??
        (matchesRouteUser(formData, routeUser) ? formData : undefined) ??
        (matchesRouteUser(cachedUser, routeUser) ? cachedUser : undefined);
    const financialUser =
        (matchesRouteUser(freshProfile, routeUser) ? freshProfile : undefined) ??
        (!isVisitedUserLoading && matchesRouteUser(staleProfile, routeUser) ? staleProfile : undefined);
    const isFinancialLoading = isVisitedUserLoading;
    const isIdentityLoading = isVisitedUserLoading;
    const isPendingAmountLoading = pendingBet === null;
    const displayAvailable = (financialUser?.creditLimit ?? 0) + (financialUser?.balance ?? 0) - totalPendingAmount;
    const isAvailableLoading = isFinancialLoading || isPendingAmountLoading;

    const balanceClass = (financialUser?.balance ?? 0) > 0
        ? 'positive'
        : (financialUser?.balance ?? 0) < 0
            ? 'negative'
            : '';

    const clearFormTransaction = () => {
        setFormTransaction({
            createDate: formatAdminFiguresShortDate(),
            agent: '',
            customer: '',
            transactionType: 'deposit',
            description: 'Customer Deposit',
            amount: '',
            createBy: '',
        });
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;

        const sanitizedValue =
            name === 'amount' ? value.replace(/[^0-9.]/g, '') : value;

        setFormTransaction(prevData => {
            const updatedData = {
                ...prevData,
                [name]: sanitizedValue,
            };

            if (name === 'transactionType') {
                const descriptions: Record<string, string> = {
                    deposit: 'Customer Deposit',
                    withdrawal: 'Customer Withdrawal',
                    DebitAdjustment: 'Debit Adjustment',
                    CreditAdjustment: 'Credit Adjustment',
                    zeroOut: 'Zero Out Balance',
                };

                updatedData.description = descriptions[value] || '';

                if (value === 'zeroOut') {
                    updatedData.amount = String(formData?.balance || 0);
                }
            }

            return updatedData;
        });
    };

    const calculateNewBalance = () => {
        const amount = parseFloat(formTransaction.amount.replace(/,/g, '')) || 0;
        const balance = Number(formData?.balance) || 0;

        let result = balance;

        switch (formTransaction.transactionType) {
            case 'deposit':
            case 'CreditAdjustment':
                result = balance + amount;
                break;

            case 'withdrawal':
            case 'DebitAdjustment':
                result = balance - amount;
                break;
            case 'zeroOut':
                result = balance - amount;
                break;
        }
        return result;
    };

    const openTransactionModal = (type: string) => {
        const descriptions: Record<string, string> = {
            deposit: 'Customer Deposit',
            withdrawal: 'Customer Withdrawal',
            DebitAdjustment: 'Debit Adjustment',
            CreditAdjustment: 'Credit Adjustment',
            zeroOut: 'Zero Out Balance',
        };

        setFormTransaction(prev => ({
            ...prev,
            transactionType: type,
            description: descriptions[type] || '',
            amount: type === 'zeroOut'
                ? String(formData?.balance ?? 0)
                : prev.amount
        }));
    };


    const saveTransaction = async () => {
        if (!formData?.id) return;
        if (isBlockedBalanceAction(balanceActor, formTransaction.transactionType)) {
            return;
        }

        try {

            await runTransaction(db, async (transaction) => {

                const userRef = doc(db, "users", formData.id!);
                const transactionRef = doc(collection(db, "transaction"));

                const userDoc = await transaction.get(userRef);

                if (!userDoc.exists()) {
                    throw new Error("User not found");
                }

                const userData = userDoc.data();

                const previousBalance = Number(userData.balance) || 0;
                const amount = parseFloat(formTransaction.amount);

                if (formTransaction.transactionType !== "zeroOut") {
                    if (!amount || amount <= 0) {
                        alert("The transaction amount cannot be 0.");
                        return;
                    }
                }

                let newBalance = previousBalance;

                switch (formTransaction.transactionType) {
                    case "deposit":
                    case "CreditAdjustment":
                        newBalance = previousBalance + amount;
                        break;

                    case "withdrawal":
                    case "DebitAdjustment":
                        newBalance = previousBalance - amount;
                        break;

                    case "zeroOut":
                        newBalance = 0;
                        break;
                }

                const creditLimit = Number(userData.creditLimit) || 0;
                const newAvailable = creditLimit + newBalance - totalPendingAmount;

                const updatedUserData = {
                    balance: newBalance,
                    available: newAvailable,
                    pending: totalPendingAmount,
                }

                transaction.update(userRef, updatedUserData);

                const newTransaction = {
                    createDate: Timestamp.fromDate(new Date()),
                    agent: formData?.parent,
                    customer: formData?.userName,
                    transactionType: formTransaction.transactionType,
                    description: formTransaction.description,
                    amount: amount,
                    createBy: currentUser!.userName,
                    userType: formData?.userType,
                    previousBalance: previousBalance,
                    newBalance: newBalance,
                    master: userData?.master
                }

                transaction.set(transactionRef, newTransaction);

                setFormData(prev => prev ? { ...prev, balance: newBalance, available: newAvailable } : null);
                setPreviousBalance(newBalance);

            });

            logUserActivity(
                currentUser!.userName,
                `Transaction: ${formTransaction?.transactionType} | Amount: ${formTransaction.amount} | Target User: ${formData?.userName}`, userIpDataAdmin
            );

            clearFormTransaction();
            await loadUserData();

            if (
                formTransaction.transactionType === 'CreditAdjustment' ||
                formTransaction.transactionType === 'DebitAdjustment'
            ) {
                requestAgentSummaryRefresh();
            }
        } catch (error) {
            console.error(error);
        }
    };

    const openBetDetailModal = () => {
        if (!canAdjustBalances) return;
        const modalElement = document.getElementById("addBalance");

        if (!modalElement) return;

        const existingInstance = Modal.getInstance(modalElement);

        if (existingInstance) {
            existingInstance.show();
        } else {
            const newModal = new Modal(modalElement);
            newModal.show();
        }
    };

    const goToConfirmModal = () => {
        if (isBlockedBalanceAction(balanceActor, formTransaction.transactionType)) {
            return;
        }

        const modal1El = document.getElementById("addBalance");
        const modal2El = document.getElementById("balanceConfirm");

        if (!modal1El || !modal2El) return;
        const modal2 = Modal.getInstance(modal2El) || new Modal(modal2El);

        modal1El.style.display = "none";

        modal2.show();
    };

    const handleConfirm = async () => {

        if (isSaving) return;

        setIsSaving(true);

        await saveTransaction();

        const modal1El = document.getElementById("addBalance");
        const modal2El = document.getElementById("balanceConfirm");

        if (!modal1El || !modal2El) return;

        const modal2 = Modal.getInstance(modal2El) || new Modal(modal2El);
        const modal1 = Modal.getInstance(modal1El) || new Modal(modal1El);

        modal2.hide();
        modal1.hide();

        modal1El.style.display = "";

        setIsSaving(false);
    };

    const handleCancelConfirm = () => {
        const modal1El = document.getElementById("addBalance");
        const modal2El = document.getElementById("balanceConfirm");

        if (!modal1El || !modal2El) return;

        const modal2 = Modal.getInstance(modal2El) || new Modal(modal2El);
        const modal1 = Modal.getInstance(modal1El) || new Modal(modal1El);

        modal2.hide();
        modal1.hide();

        modal1El.style.display = "";

        clearFormTransaction();
    };

    return (
        <>
            <div>
                <div className='container-user user-profile-card'>
                    <div className='left-admin'>
                        <div className='user-avatar' aria-hidden="true">
                            {(displayUser?.userName ?? '').slice(0, 2).toUpperCase()}
                        </div>
                        <div className='user-container'>
                            <span className='user-admin'>
                                {isIdentityLoading
                                    ? <span className="rect-placeholder placeholder-user-name" />
                                    : displayUser?.userName}
                                <span className='name-player ocult-name'>
                                    {isIdentityLoading
                                        ? <span className="rect-placeholder placeholder-user-pin" />
                                        : displayUser?.pin ? ` (${displayUser.pin})` : ''}
                                </span>
                            </span>
                            <span className='user-type ocult-name'>
                                {isIdentityLoading
                                    ? <span className="rect-placeholder placeholder-user-meta" />
                                    : `${displayUser?.userType === 'subAgent' ? 'Master' : 'Agent'} ${displayUser?.parent ?? ''}`}
                            </span>
                            <span className='name-player show-container'>
                                {isIdentityLoading
                                    ? <span className="rect-placeholder placeholder-user-pin" />
                                    : displayUser?.pin ? ` (${displayUser.pin})` : ''}
                            </span>
                        </div>
                    </div>
                    <div className="right-side">
                        <div className='left-actions'>
                            {canAdjustBalances && (
                                <button
                                    className='zero-out'
                                    onClick={() => openTransactionModal('zeroOut')}
                                    data-bs-toggle="modal"
                                    data-bs-target="#addBalance"
                                    disabled={isFinancialLoading}
                                >Zero Out</button>
                            )}
                            {returnFrom && (
                                <Link
                                    to={returnFrom.to}
                                    className="retunrFigures no-link-admin"
                                    onClick={() => logUserActivity(currentUser!.userName, returnFrom.activity, userIpDataAdmin)}
                                >
                                    <TbArrowBigLeftFilled className='icon-return-figures' />
                                    Return
                                </Link>
                            )}
                        </div>
                        <div className='right-info'>
                            <div className="user-container" onClick={() => { if (canAdjustBalances) openBetDetailModal(); }}>
                                <span className='user-balance'>Balance</span>
                                <div className='amount-user'>
                                    <DataPlaceholder
                                        loading={isFinancialLoading}
                                        className="placeholder-pending-amount"
                                    >
                                        <span className={`money line-balance ${balanceClass}`}>
                                            $ {formatNumbers(financialUser?.balance ?? 0)}
                                        </span>
                                    </DataPlaceholder>
                                    <FaCaretRight className='icon-user' />
                                </div>
                            </div>

                            <Link to="pending" state={{ from: (location.state as { from?: string })?.from }} className='user-container no-link-admin'>
                                <span className='user-balance'>Pending</span>
                                <div className='amount-user'>
                                    <DataPlaceholder
                                        loading={isPendingAmountLoading}
                                        className="placeholder-pending-amount"
                                    >
                                        <span className="money">
                                            $ {formatNumbers(totalPendingAmount)}
                                        </span>
                                    </DataPlaceholder>
                                    <FaCaretRight className='icon-user' />
                                </div>
                            </Link>

                            <div className='user-container'>
                                <span className='user-balance'>Available</span>
                                <div className='amount-user'>
                                    <DataPlaceholder
                                        loading={isAvailableLoading}
                                        className="placeholder-pending-amount"
                                    >
                                        <span className={`money available-amount ${displayAvailable < 0 ? 'negative' : ''}`}>
                                            $ {formatNumbers(displayAvailable)}
                                        </span>
                                    </DataPlaceholder>
                                    <ImCalculator className='icon-user-avaliable' />
                                    <FaCaretRight className='icon-user icon-user-mobile' />
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>

            <div className="modal fade" id="addBalance" tabIndex={-1}>
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-title-acount-admin">
                            <h4 className="modal-title fs-6">
                                <span className='letters-selected'>New Transaction</span>
                            </h4>
                        </div>
                        <div className="modal-body">
                            <div className="mb-3">
                                <span className="form-label transactionManager">Transaction</span>
                                <select name="transactionType" className="selectManager" value={formTransaction.transactionType} onChange={handleInputChange}>
                                    {canAdjustBalances && (
                                        <>
                                            <option value="deposit">Deposits</option>
                                            <option value="withdrawal">Withdrawals</option>
                                            <option value="CreditAdjustment">Credit adjustment</option>
                                            <option value="DebitAdjustment">Debit Adjustment</option>
                                            <option value="zeroOut">Zero Out</option>
                                        </>
                                    )}
                                </select>
                            </div>
                            <div className="mb-3 d-flex justify-content-between align-items-center">
                                <span className="transactionManager2">Date</span>
                                <input type="text" id="transactionDate" className="dateTransaction" readOnly value={formTransaction.createDate} />
                            </div>
                            <div className="mb-3 d-flex justify-content-between align-items-center">
                                <span className="transactionManager2">Amount</span>
                                <input type="text" name="amount" className="numberTransaction" value={
                                    formTransaction.amount
                                        ? formatNumbers(formTransaction.amount)
                                        : ''
                                }
                                    onChange={handleInputChange} disabled={formTransaction.transactionType === 'zeroOut'} autoComplete="off"
                                />
                            </div>
                            <div className="mb-3">
                                <span className="transactionManager2">Description</span>
                                <textarea name="description" className="descriptionManager" value={formTransaction.description}
                                    onChange={handleInputChange}
                                ></textarea>
                            </div>
                        </div>
                        <div className="modal-footer d-flex justify-content-between">
                            <button type="button" className="btn btn-outline-danger" data-bs-dismiss="modal" onClick={clearFormTransaction}>Cancel</button>
                            <button type="button" className="btn btn-outline-success" onClick={goToConfirmModal} disabled={!canAdjustBalances}>Continue</button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="modal fade" id="balanceConfirm" tabIndex={-1}>
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <span className="modal-title fs-6">
                                Confirm Transaction
                            </span>
                            <button type="button" className="btn-close" onClick={handleCancelConfirm}></button>
                        </div>
                        <div className="modal-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <span className="transactionManager2">Date</span>
                                <span className="transactionManager2">{formatAdminFiguresShortDate()}</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center">
                                <span className="transactionManager2">Previous Balance</span>
                                <span className="transactionManager2">${formatNumbers(formData?.balance)}</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center">
                                <span className="transactionManager2">
                                    {formTransaction.description}
                                </span>
                                <span className="transactionManager2">
                                    {(formTransaction.transactionType === 'deposit' ||
                                        formTransaction.transactionType === 'CreditAdjustment')
                                        ? '+'
                                        : '-'}
                                    $ {formatNumbers(formTransaction.amount)}
                                </span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center">
                                <span className="transactionManager2">New Balance</span>
                                <span className="transactionManager2">$ {formatNumbers(calculateNewBalance())}</span>
                            </div>
                        </div>
                        <div className="modal-footer d-flex justify-content-between">
                            <button type="button" className="btn btn-outline-danger" onClick={handleCancelConfirm}>Cancel</button>
                            <button type="button" className="btn btn-outline-success" onClick={handleConfirm}>Confirm</button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default TableUser;